/**
 * BatchBar — sticky selection bar shown when 1+ rows are selected.
 *
 * Shows selection count, clear button, native "Cambiar estado masivo" action,
 * and any consumer-provided batchActions.
 *
 * Returns null when selectedIds is empty (not rendered at all).
 *
 * @module components/list/BatchBar
 * @see FMPV-MVWC-E002-S005 I-002
 */
import React, { useState } from 'react';
import { Button } from '../../../Button/Button';
import { X } from 'lucide-react';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import type { MasterPatternViewBatchAction } from '../../types/props.types';
import type { ToastCallback } from '../../hooks/useToast';
import { useToast } from '../../hooks/useToast';

export interface BatchBarProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  selectedIds: string[];
  selectedRecords: T[];
  onClearSelection: () => void;
  permissions: MasterPatternViewPermissions;
  onBatchChangeStatus?: (records: T[]) => void | Promise<void>;
  batchActions?: MasterPatternViewBatchAction<T>[];
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

export function BatchBar<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  selectedIds,
  selectedRecords,
  onClearSelection,
  permissions,
  onBatchChangeStatus,
  batchActions = [],
  toast: toastProp,
  showInternalToasts = true,
  t,
}: BatchBarProps<T>) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const { showWarning, showError } = useToast(toastProp, showInternalToasts);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  if (selectedIds.length === 0) {
    return null;
  }

  const canChangeStatus = permissions.canChangeStatus !== false;
  const count = selectedIds.length;
  const recordWord = count !== 1 ? tt('recordsPlural', 'registros') : tt('recordWord', 'registro');
  const selectedWord = count !== 1 ? tt('selectedPlural', 'seleccionados') : tt('selectedSingular', 'seleccionado');
  const countLabel = `${count} ${recordWord} ${selectedWord}`;

  const handleChangeStatus = () => {
    if (!canChangeStatus) {
      showWarning(tt('batchNoPermissionChangeStatus', 'No tienes permisos para cambiar el estado'));
      return;
    }
    onBatchChangeStatus?.(selectedRecords);
  };

  const handleBatchAction = async (action: MasterPatternViewBatchAction<T>) => {
    setLoadingKey(action.key);
    try {
      await action.onExecute(selectedRecords);
    } catch (err) {
      showError(tt('batchActionError', 'Error al ejecutar la acción'));
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div
      data-testid="batch-bar"
      className="flex items-center gap-3 px-4 py-2 bg-primary-50 border-t border-primary-200 sticky bottom-0"
    >
      <span data-testid="batch-selection-count" className="text-sm font-medium text-primary-700">
        {countLabel}
      </span>

      <button
        data-testid="batch-clear-button"
        onClick={onClearSelection}
        className="p-1 rounded hover:bg-primary-100"
        aria-label={tt('clearSelectionAriaLabel', 'Limpiar selección')}
      >
        <X size={14} />
      </button>

      <div className="flex items-center gap-2 ml-2">
        <Button
          data-testid="batch-change-status-button"
          onClick={handleChangeStatus}
          disabled={!canChangeStatus}
        >
          {tt('batchChangeStatus', 'Cambiar estado masivo')}
        </Button>

        {batchActions.map((action) => {
          const isDisabled = action.isDisabled?.(selectedRecords) ?? false;
          const isLoading = loadingKey === action.key;
          return (
            <Button
              key={action.key}
              data-testid={`batch-action-${action.key}`}
              onClick={() => !isDisabled && !isLoading && handleBatchAction(action)}
              disabled={isDisabled || isLoading}
            >
              {action.icon && <span className="mr-1">{action.icon}</span>}
              {action.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
